/**
 * @copyright Copyright 2024 Epic Systems Corporation
 * @file Connector Management App List Row
 * @module Epic.AppOrchard.ConnectorManagement.App.AppListRow
 */

import { EditIcon } from "@chakra-ui/icons";
import { HStack, IconButton, Td, Text, Tr, Wrap, WrapItem } from "@chakra-ui/react";
import { AOLink } from "ao/components/Core";
import { useIsOverflowing } from "ao/hooks/useIsOverflowing";
import { useSiteInfoState } from "ao/state/siteInfo";
import { IColumnDefinition, ISiteInfo, IUserSecurity } from "ao/types";
import { IConnectorAppListItem } from "ao/types/connectorManagement";
import { FC, memo, useRef } from "react";
import { MdMergeType } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { getConnectorAppEditRelativeUrl, getConnectorAppEditUrl } from "routes/connectorAppEdit";
import { getConnectorAppViewRelativeUrl } from "routes/connectorAppView";
import { getConnectorAppMergeUrl } from "routes/connectorMergeApps";
import { getConnectorAppColumnText, getConnectorAppColumnTitle } from "../../data/columns";
import { AppListColumnKey, Colors, ReviewStatus } from "../../data/constants";
import { ConnectorsAppTag } from "../Shared/ConnectorsAppTag";
import { ConnectorsAppTagList } from "../Shared/ConnectorsAppTagList";

interface IRowProps {
	userSecurity: IUserSecurity;
	connectorApp: IConnectorAppListItem;
	columns: IColumnDefinition[];
	siteInfo: ISiteInfo;
}

export const AppListRow: FC<IRowProps> = memo((props) => {
	const { userSecurity, connectorApp, columns } = props;
	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);

	const location = useLocation();
	const navigate = useNavigate();

	const isConnectorsAdmin = userSecurity.canManageConnectorsAppsOrgs && siteInfo.isAdminSite;
	const isMerged = connectorApp.reviewStatus.id === ReviewStatus.Merged;
	const isClientApp = connectorApp.clientAppId !== null;

	const ActionButtonTile = {
		editButtonTitle: "Edit this app",
		mergeButtonTile: isMerged
			? "Cannot Merge Previously Merged Apps"
			: isClientApp
			? "Cannot Merge Client Apps"
			: "Merge App",
	};

	const categoryContainerRef = useRef<HTMLDivElement>(null);
	const categoryContentRef = useRef<HTMLDivElement>(null);

	const isCategoryOverflowing = useIsOverflowing(categoryContainerRef, categoryContentRef, 7);

	return (
		<Tr display="table-row" color="inherit">
			{columns.map((column) => {
				return column.key === AppListColumnKey.id ? (
					<Td
						key={column.key}
						title={isConnectorsAdmin ? "Go to app edit page" : "Go to app view page"}
						w={column.width}
						minWidth={column.minWidth}
						maxWidth={column.maxWidth}
					>
						{isConnectorsAdmin ? (
							<AOLink url={getConnectorAppEditRelativeUrl(connectorApp.id)}>
								{getConnectorAppColumnText(connectorApp, column)}
							</AOLink>
						) : (
							<AOLink url={getConnectorAppViewRelativeUrl(connectorApp.id)}>
								{getConnectorAppColumnText(connectorApp, column)}
							</AOLink>
						)}
					</Td>
				) : column.key === AppListColumnKey.clientAppId ? (
					<Td
						key={column.key}
						title={getConnectorAppColumnTitle(connectorApp, column)}
						w={column.width}
						minWidth={column.minWidth}
						maxWidth={column.maxWidth}
					>
						{connectorApp.clientAppId &&
						(userSecurity.isEpicAdministrator || userSecurity.isSuperAdministrator) ? (
							<AOLink url={`/Developer/Review?appId=${connectorApp.clientAppId}`}>
								{getConnectorAppColumnText(connectorApp, column)}
							</AOLink>
						) : (
							<Text>{getConnectorAppColumnText(connectorApp, column)}</Text>
						)}
					</Td>
				) : column.key === AppListColumnKey.name ? (
					<Td
						key={column.key}
						title={getConnectorAppColumnText(connectorApp, column)}
						w={column.width}
						minWidth={column.minWidth}
						maxWidth={column.maxWidth}
					>
						<Text>{getConnectorAppColumnText(connectorApp, column)}</Text>
						<ConnectorsAppTagList connectorApp={connectorApp} />
					</Td>
				) : column.key === AppListColumnKey.appCategories ? (
					<Td
						key={column.key}
						w={column.width}
						minWidth={column.minWidth}
						maxWidth={column.maxWidth}
					>
						{connectorApp.appCategories?.length ? (
							<>
								<div
									ref={categoryContainerRef}
									style={{
										maxHeight: "90px",
										overflow: "hidden",
										display: "block",
									}}
								>
									<Wrap ref={categoryContentRef} width="100%">
										{connectorApp.appCategories.map((record, index) => (
											<WrapItem
												as={ConnectorsAppTag}
												title={record.categoryName}
												key={index}
												label={record.categoryName}
												colorScheme={Colors[record.categoryId % Colors.length]}
												variant="subtle"
											/>
										))}
									</Wrap>
								</div>
								{isCategoryOverflowing && (
									<Text
										fontSize="xs"
										color="gray.500"
										mt={1}
										title={connectorApp.appCategories
											.map((c) => c.categoryName)
											.join(", ")}
										textDecoration="underline"
									>
										More categories not shown
									</Text>
								)}
							</>
						) : null}
					</Td>
				) : column.key === AppListColumnKey.version ? (
					<Td
						key={column.key}
						title={getConnectorAppColumnTitle(connectorApp, column)}
						w={column.width}
						minWidth={column.minWidth}
						maxWidth={column.maxWidth}
					>
						<Text>{getConnectorAppColumnText(connectorApp, column)}</Text>
					</Td>
				) : column.key === AppListColumnKey.actionLinks ? (
					<Td
						key={column.key}
						title={getConnectorAppColumnTitle(connectorApp, column)}
						w={column.width}
						minWidth={column.minWidth}
						maxWidth={column.maxWidth}
					>
						<HStack wrap="wrap">
							<>
								<IconButton
									icon={<EditIcon />}
									title={ActionButtonTile.editButtonTitle}
									aria-label={ActionButtonTile.editButtonTitle}
									onClick={() =>
										navigate(getConnectorAppEditUrl(connectorApp.id), {
											state: { from: location.pathname },
										})
									}
								/>
								{isConnectorsAdmin && (
									<IconButton
										title={ActionButtonTile.mergeButtonTile}
										aria-label={ActionButtonTile.mergeButtonTile}
										onClick={() => {
											navigate(getConnectorAppMergeUrl(connectorApp.id), {
												state: { from: location.pathname },
											});
										}}
										isDisabled={isMerged || isClientApp}
										icon={<MdMergeType />}
									/>
								)}
							</>
						</HStack>
					</Td>
				) : (
					<Td
						key={column.key}
						title={getConnectorAppColumnTitle(connectorApp, column)}
						w={column.width}
						minWidth={column.minWidth}
						maxWidth={column.maxWidth}
					>
						<Text>{getConnectorAppColumnText(connectorApp, column)}</Text>
					</Td>
				);
			})}
		</Tr>
	);
});
